import React from "react";
import { ArrowUpRight, CircleDot, LayoutGrid, Users, Milestone, Tag, Building, Component, GitBranch } from "lucide-react";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ENTITY_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure";
import { EntityRecord } from "@/types/entity";

// --- Meta Item Config Types ---

interface BaseMetaItemConfig {
  label: string;
  placeholder?: string;
  icon?: React.ReactNode;
}

export interface ComboboxMetaItemConfig extends BaseMetaItemConfig {
  type?: "combobox";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  showBadges?: boolean;
  multiSelect?: boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabled?: (record: EntityRecord, context: any) => boolean;
  createOnUnmatched?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    viewManager?: any;
    value?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    project?: any;
  }) => void;
  optionItemActionRenderer?: (params: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    record: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorHandler: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    entityManager: any;
  }) => React.ReactNode;
}

export interface InputMetaItemConfig extends BaseMetaItemConfig {
  type: "input";
  fieldName: string;
  inputType?: "text" | "number";
}

export interface IconMetaItemConfig extends BaseMetaItemConfig {
  type: "icon";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export interface TagsMetaItemConfig extends BaseMetaItemConfig {
  type: "tags";
  storeKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterFn: (record: any) => boolean;
  fieldConfig: {
    name: string;
    valueField: string;
    displayField: string;
  };
}

export type MetaItemConfig = ComboboxMetaItemConfig | InputMetaItemConfig | IconMetaItemConfig | TagsMetaItemConfig;

type EntityTypeKey = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

// --- Editor Meta Items Data ---

export const EDITOR_META_ITEMS: { [K in EntityTypeKey]: MetaItemConfig[] } = {
  [ENTITY_TYPES.TASK]: [
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TASK_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TASK_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Phase",
      label: "Phase",
      placeholder: "Select phase",
      icon: <Milestone className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "phaseId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TASK_TAGS
        }});
      }
    },
    {
      storeKey: STORE_KEYS.clientStore,
      filterFn: () => true,
      label: "Client",
      placeholder: "Select client",
      icon: <Building className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "clientId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({ value, record, project }) => {
        const newRecord = project.clientsStore.createRecord({ name: value });
        project.clientsStore.add(newRecord);
        record.set("clientId", newRecord.id);
      },
      optionItemActionRenderer: ({ record, entityManager }) => {
        return (
          <button
            type="button"
            className="opacity-0 group-hover/command-item:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-sm p-1 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              entityManager.openEntityManager({ entityType: ENTITY_TYPES.CLIENT, entityId: record.id });
            }}
          >
            <ArrowUpRight className="size-3" />
          </button>
        );
      }
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.taskTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ],
  [ENTITY_TYPES.RESOURCE]: [
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.RESOURCE_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.RESOURCE_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      type: "icon",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Icon",
      label: "Icon",
      placeholder: "Select icon",
      fieldConfig: {
        name: "iconId",
        valueField: "id",
        displayField: "tagValue"
      }
    },
    {
      storeKey: STORE_KEYS.resourceTypeStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Type",
      placeholder: "Select type",
      icon: <Component className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "typeId",
        valueField: "id",
        displayField: "description"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.RESOURCE_TYPES
        }});
      }
    },
    {
      type: "input",
      label: "Sub Type",
      placeholder: "Enter sub type",
      icon: <GitBranch className="size-4" />,
      fieldName: "jobTitle",
      inputType: "text"
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.resourceTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ],
  [ENTITY_TYPES.CLIENT]: [
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Status",
      label: "Status",
      placeholder: "Select status",
      icon: <CircleDot className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "statusId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.CLIENT_STATUSES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Category",
      label: "Category",
      placeholder: "Select category",
      icon: <LayoutGrid className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "categoryId",
        valueField: "id",
        displayField: "tagValue"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.CLIENT_CATEGORIES
        }});
      }
    },
    {
      storeKey: STORE_KEYS.boardTeamsStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (_record: any) => true,
      label: "Team",
      placeholder: "Select team",
      icon: <Users className="size-4" />,
      showBadges: true,
      fieldConfig: {
        name: "teamId",
        valueField: "id",
        displayField: "name"
      },
      createOnUnmatched: ({ viewManager }) => {
        viewManager.openAnonymousViewByType({ viewType: VIEW_TYPES.settings, viewName: "Settings", otherParams: {
          setting: SETTING_KEYS.TEAMS_GENERAL
        }});
      }
    },
    {
      type: "tags",
      storeKey: STORE_KEYS.clientTagValuesStore,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filterFn: (record: any) => record.tagSet === "Tag",
      label: "Tags",
      icon: <Tag className="size-4" />,
      fieldConfig: {
        name: "tagsId",
        valueField: "id",
        displayField: "tagValue"
      }
    }
  ]
};
